// Días festivos (feriados) nacionales y locales: en feriado el trabajo se paga
// como extraordinaria 100% (Arts. 55.4/65 CT) y la ausencia no genera falta.
// Nómina → Configuración → Feriados. Solo admin.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { Campo, Modal, fieldCls, nomRpc, useDialogoEnLinea, type FeriadoRow } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

const ANIO_ACTUAL = new Date().getFullYear();

const diaSemana = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(LOCALE, { weekday: 'long' });

const FORM_VACIO = { id: '', fecha: '', nombre: '', ambito: 'local', observacion: '' };

export function Feriados() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [anio, setAnio] = useState(ANIO_ACTUAL);
  const { data: rows = [], isFetching } = useQuery<FeriadoRow[]>({
    queryKey: ['nom-feriados', anio],
    queryFn: () => nomRpc<FeriadoRow[]>('nom_feriados', { _anio: anio }),
  });

  const [form, setForm] = useState(FORM_VACIO);
  const [abierto, setAbierto] = useState(false);

  const invalidar = () => void qc.invalidateQueries({ queryKey: ['nom-feriados'] });

  const copiar = useMutation({
    mutationFn: () => nomRpc<{ insertados: number }>('nom_feriados_copiar_nacionales', { _anio: anio }),
    onSuccess: (r) => {
      toast.success(r.insertados > 0
        ? `${r.insertados} feriados nacionales de ${anio} registrados`
        : `Los feriados nacionales de ${anio} ya estaban registrados`);
      invalidar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_feriado_guardar', {
      _data: {
        id: form.id || null, fecha: form.fecha, nombre: form.nombre.trim(),
        ambito: form.ambito, observacion: form.observacion.trim() || null,
      },
    }),
    onSuccess: () => { toast.success('Feriado guardado'); setAbierto(false); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => nomRpc('nom_feriado_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Feriado eliminado'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const headersExp = ['N°', 'Fecha', 'Día', 'Nombre', 'Ámbito', 'Observación'];
  const filasExport: Celda[][] = rows.map((r, i): Celda[] => [
    i + 1, r.fecha.slice(0, 10), diaSemana(r.fecha.slice(0, 10)), r.nombre,
    r.ambito === 'nacional' ? 'Nacional' : 'Local', r.observacion ?? '',
  ]);

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Feriados</h1>
        <div className="flex items-center gap-2">
          <input type="number" className={`${fieldCls} w-24`} value={anio} onChange={(e) => setAnio(Number(e.target.value))} aria-label="Año" />
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
          <ExportarBotones nombre={`feriados-${fechaLocal()}`} titulo="Feriados"
            headers={headersExp} filas={filasExport} colsDerecha={[0]} disabled={rows.length === 0} />
          <Button variant="outline" isLoading={copiar.isPending} onClick={() => copiar.mutate()}>
            <CalendarDays className="w-4 h-4 mr-1" /> Copiar nacionales {anio}
          </Button>
          <Button onClick={() => { setForm(FORM_VACIO); setAbierto(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Nuevo feriado
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        En un feriado, las horas trabajadas se pagan como <strong>extraordinarias al 100%</strong> (Arts. 55.4 y 65 CT)
        y la ausencia <strong>no genera falta</strong>. "Copiar nacionales" siembra el calendario de la Ley Orgánica
        de Feriados con sus traslados (martes → lunes; miércoles/jueves → viernes; fin de semana → viernes/lunes);
        los feriados locales de tu cantón agrégalos manualmente.
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Feriados {anio} ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Día</th><th className="py-2 pr-3">Nombre</th>
              <th className="py-2 pr-3">Ámbito</th><th className="py-2 pr-3">Observación</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && (
                <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">
                  Sin feriados en {anio}: copia los nacionales o registra uno manualmente
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{r.fecha.slice(0, 10)}</td>
                  <td className="py-2 pr-3 capitalize">{diaSemana(r.fecha.slice(0, 10))}</td>
                  <td className="py-2 pr-3">{r.nombre}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${r.ambito === 'nacional' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {r.ambito === 'nacional' ? 'Nacional' : 'Local'}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{r.observacion ?? ''}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar"
                      onClick={() => {
                        setForm({ id: r.id, fecha: r.fecha.slice(0, 10), nombre: r.nombre, ambito: r.ambito, observacion: r.observacion ?? '' });
                        setAbierto(true);
                      }}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar el feriado "${r.nombre}" (${r.fecha.slice(0, 10)})?`, { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(r.id); }}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={form.id ? 'Editar feriado' : 'Nuevo feriado'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha">
                <input type="date" className={fieldCls} value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
              </Campo>
              <Campo label="Ámbito">
                <select className={fieldCls} value={form.ambito} onChange={(e) => setForm({ ...form, ambito: e.target.value })}>
                  <option value="local">Local (cantonal/provincial)</option>
                  <option value="nacional">Nacional</option>
                </select>
              </Campo>
            </div>
            <Campo label="Nombre">
              <input className={fieldCls} value={form.nombre} placeholder="Ej. Fundación de Quito" onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
            </Campo>
            <Campo label="Observación (opcional)">
              <input className={fieldCls} value={form.observacion} onChange={(e) => setForm({ ...form, observacion: e.target.value })} />
            </Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.fecha || !form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
